import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import {
  audioDeviceItemSx,
  workbenchOverlayLabelSx,
  workbenchOverlaySx
} from "./dialstack-softphone-widget.styles";

type Device = { deviceId: string; label: string; isDefault: boolean };
type DeviceList = { inputs: Device[]; outputs: Device[]; supported: boolean };

const deviceName = (device: Device, index: number, kind: string) =>
  device.label ||
  (device.isDefault ? `Default ${kind}` : `${kind} ${index + 1}`);

// Microphone and speaker choice for the call in progress, read from and set
// on the SDK's phone (`listAudioDevices`, `setAudioInputDevice`,
// `setAudioOutputDevice`). Replaces the SDK's device overlay (ENG-2226).
export default function AudioDeviceMenu() {
  const { webrtcPhone } = useSoftphone();
  const [devices, setDevices] = useState<DeviceList | null>(null);
  const [input, setInput] = useState<string | null>(null);
  const [output, setOutput] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!webrtcPhone) return undefined;
    webrtcPhone
      .listAudioDevices()
      .then((list) => {
        if (cancelled) return;
        setDevices(list);
        setInput(
          (current) =>
            current ??
            list.inputs.find((device) => device.isDefault)?.deviceId ??
            null
        );
        setOutput(
          (current) =>
            current ??
            list.outputs.find((device) => device.isDefault)?.deviceId ??
            null
        );
      })
      .catch(() => {
        if (!cancelled)
          setDevices({ inputs: [], outputs: [], supported: false });
      });
    return () => {
      cancelled = true;
    };
  }, [webrtcPhone]);

  const chooseInput = (deviceId: string) => {
    setInput(deviceId);
    webrtcPhone?.setAudioInputDevice(deviceId).catch(() => undefined);
  };
  const chooseOutput = (deviceId: string) => {
    setOutput(deviceId);
    webrtcPhone?.setAudioOutputDevice(deviceId);
  };

  if (!devices) {
    return (
      <Box data-testid="audio-device-menu" sx={workbenchOverlaySx}>
        <CircularProgress
          aria-label="Finding audio devices"
          color="inherit"
          size={18}
        />
      </Box>
    );
  }
  if (!devices.supported) {
    return (
      <Box data-testid="audio-device-menu" sx={workbenchOverlaySx}>
        <Typography sx={workbenchOverlayLabelSx}>
          This browser does not let the phone choose audio devices.
        </Typography>
      </Box>
    );
  }
  return (
    <Box data-testid="audio-device-menu" sx={workbenchOverlaySx}>
      <Typography component="p" sx={workbenchOverlayLabelSx}>
        Microphone
      </Typography>
      <Box role="radiogroup" aria-label="Microphone">
        {devices.inputs.map((device, index) => (
          <MenuItem
            key={device.deviceId}
            role="radio"
            aria-checked={device.deviceId === input}
            selected={device.deviceId === input}
            className="suno-phone-no-drag"
            onClick={() => chooseInput(device.deviceId)}
            sx={audioDeviceItemSx}
          >
            {deviceName(device, index, "microphone")}
          </MenuItem>
        ))}
      </Box>
      <Typography component="p" sx={[workbenchOverlayLabelSx, { mt: 1 }]}>
        Speaker
      </Typography>
      <Box role="radiogroup" aria-label="Speaker">
        {devices.outputs.map((device, index) => (
          <MenuItem
            key={device.deviceId}
            role="radio"
            aria-checked={device.deviceId === output}
            selected={device.deviceId === output}
            className="suno-phone-no-drag"
            onClick={() => chooseOutput(device.deviceId)}
            sx={audioDeviceItemSx}
          >
            {deviceName(device, index, "speaker")}
          </MenuItem>
        ))}
      </Box>
    </Box>
  );
}
